import * as React from "react";
import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Share2, Download, ExternalLink, Check, SquareUserRound } from "lucide-react";
import copy from "copy-to-clipboard";
import { toast } from "sonner";

import { Button, Switch, Input } from "@/components/ui";
import { cn } from "@/lib/utils";

interface PlayerQRCodeProps {
  id: string;
  value: string;
  qrLogoSrc?: string | null;
  size?: number;
  title?: string;
  text?: string;
  downloadFileName?: string;
}

export const PlayerQRCode: React.FC<PlayerQRCodeProps> = ({
  id,
  value,
  qrLogoSrc,
  size = 200,
  title = "Leave a Review",
  text = "I would appreciate your recent feedback. Please take a moment to leave a review!",
  downloadFileName = "qr-code.png",
}) => {
  const [includeAvatar, setIncludeAvatar] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);
  const [base64Logo, setBase64Logo] = useState<string | null>(null);

  // Convert qrLogoSrc to an inline Base64 data URL to bypass browser security constraints on SVG-to-Canvas exports
  useEffect(() => {
    if (!qrLogoSrc) {
      setBase64Logo(null);
      return;
    }

    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        try {
          const dataUrl = canvas.toDataURL("image/png");
          setBase64Logo(dataUrl);
        } catch (err) {
          console.error("CORS block or canvas tainted while inlining profile image", err);
          setBase64Logo(qrLogoSrc); // Fallback to raw URL
        }
      }
    };
    img.onerror = () => {
      setBase64Logo(qrLogoSrc);
    };
    img.src = qrLogoSrc;
  }, [qrLogoSrc]);

  const activeLogoSrc = base64Logo || qrLogoSrc || "";

  const imageSettings =
    includeAvatar && activeLogoSrc
      ? {
          src: activeLogoSrc,
          x: undefined,
          y: undefined,
          height: 60,
          width: 60,
          excavate: true,
        }
      : undefined;

  const handleCopyLink = () => {
    if (value) {
      copy(value);
      toast.success("Link copied to clipboard!");
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  const handleNativeShare = async () => {
    if (!value) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url: value,
        });
      } catch (err) {
        // User cancelled, ignore
      }
    } else {
      handleCopyLink();
    }
  };

  const downloadQrCode = () => {
    const svg = document.getElementById(id);
    if (!svg) return;

    // Create a high-resolution canvas (1000x1000) so the PNG is incredibly crisp when printed or zoomed!
    const canvas = document.createElement("canvas");
    canvas.width = 1000;
    canvas.height = 1000;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Fill background with solid white so the QR code maintains high contrast (prevents transparent background issues)
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const svgElement = svg.cloneNode(true) as SVGElement;
    svgElement.setAttribute("width", "1000");
    svgElement.setAttribute("height", "1000");

    const svgString = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new window.Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 1000, 1000);
      URL.revokeObjectURL(svgUrl);

      try {
        const pngUrl = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;

        // Ensure filename has a .png extension
        const pngFileName = downloadFileName.replace(/\.svg$/, ".png");
        downloadLink.download = pngFileName;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        toast.success("PNG QR Code downloaded successfully!");
      } catch (err) {
        console.error("Failed to generate PNG URL", err);
        toast.error("Failed to export as PNG.");
      }
    };
    img.onerror = err => {
      console.error("Failed to load SVG into image", err);
      toast.error("Failed to render QR Code.");
      URL.revokeObjectURL(svgUrl);
    };

    img.src = svgUrl;
  };

  return (
    <div className="flex flex-col items-center gap-5 w-full min-w-0 relative">
      {/* Floating Top-Right Mini Pill Toggle (Parent level DOM, sits fully outside QR box) */}
      {qrLogoSrc && (
        <div className="absolute top-0 right-0 sm:right-1 bg-white/95 backdrop-blur-xs border border-slate-200/80 rounded-full pl-2.5 pr-1 flex items-center gap-1 select-none h-7 animate-in fade-in duration-300">
          <SquareUserRound
            className={`size-3.5 shrink-0 transition-colors duration-250 ${
              includeAvatar ? "text-indigo-600" : "text-slate-400"
            }`}
          />
          <Switch checked={includeAvatar} onCheckedChange={setIncludeAvatar} className="scale-[0.65] origin-center" />
        </div>
      )}

      {/* QR Code Canvas Frame */}
      <div className="bg-white p-2 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center shrink-0 select-none animate-in fade-in zoom-in-95 duration-200">
        <QRCodeSVG id={id} value={value} size={size} level="H" marginSize={1} imageSettings={imageSettings} />
      </div>

      {/* Link Input & Open Link row */}
      <div className="w-full flex gap-2">
        <Input
          readOnly
          value={value}
          className="flex-1 bg-slate-50 border-slate-200 font-mono select-all h-10 py-2.5 px-3 rounded-xl focus:ring-0 focus:border-slate-200"
        />
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-800 transition-colors"
          title="Open link in new tab"
        >
          <ExternalLink className="size-4.5" />
        </a>
      </div>

      {/* Full-width Copy to Clipboard Button */}
      <Button
        onClick={handleCopyLink}
        variant={copied ? "default" : "outline"}
        className={cn(
          "w-full h-11 rounded-xl transition-all duration-200 select-none flex items-center justify-center gap-2 text-sm font-semibold border-slate-200",
          copied
            ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
            : "text-slate-700 hover:bg-slate-50"
        )}
        type="button"
      >
        {copied ? (
          <>
            <Check className="size-4.5 shrink-0 animate-bounce" />
            <span>Copied to Clipboard!</span>
          </>
        ) : (
          <>
            <Copy className="size-4 shrink-0 text-slate-500" />
            <span>Copy Link to Clipboard</span>
          </>
        )}
      </Button>

      {/* Quick Actions Row */}
      <div className="grid grid-cols-2 gap-3 w-full">
        <Button
          variant="outline"
          className="flex items-center justify-center gap-2 text-sm font-semibold h-11 w-full text-slate-700 border-slate-200 hover:bg-slate-50 transition-colors"
          onClick={downloadQrCode}
        >
          <Download className="size-4 shrink-0 text-slate-500" />
          Download QR
        </Button>
        <Button
          variant="default"
          className="flex items-center justify-center gap-2 text-sm font-semibold h-11 w-full"
          onClick={handleNativeShare}
        >
          <Share2 className="size-4 shrink-0" />
          {navigator.share ? "Share Link" : "Copy Link"}
        </Button>
      </div>
    </div>
  );
};
export default PlayerQRCode;
